import type { VerifiedProfitDataContract } from "./profit-dashboard-config";

export interface ProfitRelation {
  id: string;
  agentId: string;
  agentName: string;
  account: string;
  broker: string;
  server: string;
  symbols: string[];
  copyMode: string;
  copyValue: number | null;
  positions: number | null;
  status: string;
  lastCommandId: string;
  updatedAt: number | null;
  pnl: number | null;
}

export type ProfitStatusFilter = "all" | "active" | "paused" | "stopped";
export interface ProfitAmount { minor: bigint | null; currency: string | null; reason: string }
const text = (value: unknown) => typeof value === "string" ? value.trim() : "";
const finiteNumber = (value: unknown) => typeof value === "number" && Number.isFinite(value) ? value : null;

export class ProfitDataError extends Error {}

export function normalizeProfitRelations(response: unknown): ProfitRelation[] {
  if (!response || typeof response !== "object") throw new ProfitDataError("跟单数据结构异常");
  const { items, total } = response as { items?: unknown; total?: unknown };
  if (!Array.isArray(items) || total !== items.length) throw new ProfitDataError("跟单数据不完整，请刷新重试");
  const ids = new Set<string>();
  return items.map((item) => {
    if (!item || typeof item !== "object") throw new ProfitDataError("跟单数据结构异常");
    const row = item as Record<string, unknown>;
    const id = text(row.relation_id);
    if (!id || ids.has(id)) throw new ProfitDataError("跟单关系标识异常，请刷新重试");
    ids.add(id);
    const positions = finiteNumber(row.current_positions);
    const updated = finiteNumber(row.updated_at);
    const account = text(row.follower_account_masked);
    return {
      id, agentId: text(row.agent_id), agentName: text(row.agent_name),
      // Never render an accidentally unmasked account number.
      account: /[*•]/.test(account) ? account : "",
      broker: text(row.broker), server: text(row.server),
      symbols: Array.isArray(row.symbols) ? row.symbols.filter((s): s is string => typeof s === "string" && !!s) : [],
      copyMode: text(row.copy_mode), copyValue: finiteNumber(row.copy_value),
      positions: positions !== null && Number.isInteger(positions) && positions >= 0 ? positions : null,
      status: text(row.status), lastCommandId: text(row.last_command_id),
      updatedAt: updated !== null && updated > 0 && Number.isFinite(new Date(updated * 1000).getTime()) ? updated : null,
      pnl: finiteNumber(row.total_pnl),
    };
  });
}

export function verifiedProfitCurrency(contract: VerifiedProfitDataContract | null, environmentId: string): string | null {
  return contract && environmentId && contract.environmentId === environmentId &&
    contract.evidenceRef.trim() && /^[A-Z]{3}$/.test(contract.currency) ? contract.currency : null;
}

/** Round each displayed amount to 2 decimals, half away from zero, using decimal
 * digits rather than floating-point addition. Reject unsafe source precision. */
export function profitMinorUnits(value: number | null): bigint | null {
  if (value === null || !Number.isFinite(value) || Math.abs(value) > Number.MAX_SAFE_INTEGER / 100) return null;
  const [mantissa, exponent = "0"] = Math.abs(value).toString().split("e");
  const [whole, fraction = ""] = mantissa.split(".");
  let digits = BigInt(whole + fraction);
  const scale = Number(exponent) - fraction.length + 2;
  if (scale >= 0) digits *= 10n ** BigInt(scale);
  else {
    const divisor = 10n ** BigInt(-scale);
    digits = (digits + divisor / 2n) / divisor;
  }
  return value < 0 ? -digits : digits;
}

export function relationProfit(row: ProfitRelation, contract: VerifiedProfitDataContract | null, environmentId: string): ProfitAmount {
  const currency = verifiedProfitCurrency(contract, environmentId);
  if (!currency) return { minor: null, currency: null, reason: "收益待核实" };
  const minor = profitMinorUnits(row.pnl);
  return { minor, currency, reason: minor === null ? "收益数据不完整" : "" };
}

export function summarizeProfit(rows: ProfitRelation[], contract: VerifiedProfitDataContract | null, environmentId: string) {
  const currency = verifiedProfitCurrency(contract, environmentId);
  let profit: ProfitAmount = { minor: null, currency, reason: rows.length ? "收益待核实" : "暂无收益数据" };
  if (currency && rows.length) {
    const values = rows.map((row) => profitMinorUnits(row.pnl));
    profit = values.some((v) => v === null)
      ? { minor: null, currency, reason: "收益数据不完整" }
      : { minor: values.reduce<bigint>((sum, value) => sum + (value as bigint), 0n), currency, reason: "" };
  }
  return { profit, active: rows.filter((row) => row.status === "active").length, total: rows.length };
}

export function filterProfitRelations(rows: ProfitRelation[], status: ProfitStatusFilter) {
  return rows.filter((row) => status === "all" || row.status === status);
}

export function sortProfitRelations(rows: ProfitRelation[], direction: "asc" | "desc", enabled: boolean) {
  if (!enabled) return [...rows];
  return [...rows].sort((a, b) => {
    const av = profitMinorUnits(a.pnl), bv = profitMinorUnits(b.pnl);
    if (av === null && bv !== null) return 1;
    if (bv === null && av !== null) return -1;
    if (av !== null && bv !== null && av !== bv) return (av < bv ? -1 : 1) * (direction === "asc" ? 1 : -1);
    return a.id.localeCompare(b.id);
  });
}

export function formatProfitAmount(amount: ProfitAmount): string {
  if (amount.minor === null || !amount.currency) return "—";
  const value = amount.minor < 0n ? -amount.minor : amount.minor;
  const whole = (value / 100n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const decimals = (value % 100n).toString().padStart(2, "0");
  return `${amount.minor > 0n ? "+" : amount.minor < 0n ? "-" : ""}${whole}.${decimals} ${amount.currency}`;
}

export function profitStatusLabel(status: string) {
  return ({ active: "跟单中", paused: "已暂停", stopped: "已停止" } as Record<string, string>)[status] || "其他";
}

export function formatRelationTime(timestamp: number | null) {
  return timestamp === null ? "—" : new Date(timestamp * 1000).toLocaleString("zh-CN", { hour12: false });
}
